import styled from '@emotion/styled';
import React, { useEffect, useRef, useState } from 'react';

interface CommitTextFieldProps {
  value: string | undefined;
  onCommit: (value: string) => void;
  multiline?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const CommitTextField: React.FC<CommitTextFieldProps> = ({
  value,
  onCommit,
  multiline,
  placeholder,
  disabled,
}) => {
  const [draft, setDraft] = useState<string>(value ?? '');
  const draftRef = useRef<string>(draft);
  const cancelledRef = useRef<boolean>(false);
  const commitRef = useRef<(next: string) => void>(() => {});
  const inputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  commitRef.current = (next: string) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    if (next !== (value ?? '')) onCommit(next);
  };

  useEffect(
    () => () => {
      commitRef.current(draftRef.current);
    },
    [],
  );

  const commit = () => commitRef.current(draftRef.current);

  const cancel = () => {
    cancelledRef.current = true;
    setDraft(value ?? '');
    inputRef.current?.blur();
    textareaRef.current?.blur();
  };

  if (multiline) {
    return (
      <TextArea
        ref={textareaRef}
        value={draft}
        rows={3}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
      />
    );
  }
  return (
    <Input
      ref={inputRef}
      type="text"
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
    />
  );
};

export default CommitTextField;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 4px 6px;
  font-size: 13px;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  outline: none;

  &:focus {
    border-color: #9ca3af;
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-width: 0;
  padding: 4px 6px;
  font-size: 13px;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  outline: none;
  resize: vertical;
  font-family: inherit;

  &:focus {
    border-color: #9ca3af;
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
    resize: none;
  }
`;
